<template>
    <div class="sbs-select">
        <SbsPopup
            v-bind="popupData"
            v-model="isPopupVisible">
            <template #content>
                <SbsScrollWrapper
                    v-if="items.length"
                    fadeCurtainSize="25"
                    hideTopCurtain
                    @scroll="scrollHandler"
                    @scroll:reachEnd="scrollReachEndHandler"
                    @scroll:reachStart="scrollReachStartHandler">
                    <!-- @slot Слот для вставки контента перед списком элементов -->
                    <slot name="prepend"></slot>

                    <SbsSelectList
                        v-model="selectModel"
                        class="sbs-select__list"
                        :class="selectListGroupClass"
                        :disabled="disabled"
                        :mandatory="mandatory">
                        <!-- @slot Слот для списка элементов -->
                        <slot
                            name="list"
                            v-bind="{
                                items,
                                isItemSelected,
                            }">
                            <SbsSelectListItem
                                v-for="(item, index) in items"
                                ref="focusedItem"
                                :key="index"
                                class="sbs-select__list-item"
                                :modelValue="item.value"
                                selectedClass="selected"
                                labelClass="sbs-select__list-item-label"
                                hideControl>
                                <template #default>
                                    <div class="sbs-select__list-item-inner">
                                        <span class="sbs-select__list-item-text">{{ item.label }}</span>
                                        <SbsIcon
                                            v-bind="listItemIconData"
                                            class="sbs-select__list-item-icon" />
                                    </div>
                                </template>
                            </SbsSelectListItem>
                        </slot>
                    </SbsSelectList>

                    <!-- @slot Слот для вставки контента после списка элементов -->
                    <slot name="append"></slot>
                </SbsScrollWrapper>

                <template v-else>
                    <!-- @slot Слот при отсутствии элементов -->
                    <slot name="empty"></slot>
                </template>
            </template>

            <div class="sbs-select__toggler">
                <!-- @slot Слот по-умолчанию для активатора -->
                <slot
                    v-bind="{
                        disabled,
                        opened: isPopupVisible,
                        selected: selectedItems,
                    }">
                    <SbsButton
                        v-bind="togglerData"
                        class="sbs-select__toggler-button"
                        @click.stop="isPopupVisible = !isPopupVisible">
                        <template #append>
                            <SbsIcon
                                v-bind="togglerIconData"
                                class="sbs-select__toggler-icon trs" />
                        </template>
                    </SbsButton>
                </slot>
            </div>
        </SbsPopup>
    </div>
</template>

<script>
import themesMixin from '../../mixins/themes.js';
import { AllColors } from '../../utils/colors.js';
import { AllPositions } from '../../utils/positions.js';
import { AllSizes } from '../../utils/sizes.js';
import { Keys } from '../../utils/keys.js';

const DEFAULT_POPUP_OPTIONS = {
    position: AllPositions.BOTTOM_START,
    showCloseButton: false,
    width: 250,
    maxHeight: 200,
};

export default {
    name: 'SbsSelect',
    mixins: [themesMixin],
    emits: [
        'update:modelValue',
        'update:opened',
        'scroll',
        'scroll:reachEnd',
        'scroll:reachStart',
        'focusedIndex:change',
    ],
    data() {
        return {
            focusedIndex: -1,
        };
    },
    computed: {
        selectModel: {
            get() {
                return this.modelValue;
            },
            set(value) {
                /**
                 * Эмит события `update:modelValue`, отвечает за изменение списка селекта
                 */
                this.$emit('update:modelValue', value);

                if (!Array.isArray(this.selectModel)) {
                    this.isPopupVisible = false;
                }
            },
        },
        selectedItems() {
            if (Array.isArray(this.selectModel)) {
                return this.selectModel.map(item => this.items
                    .find(({ value }) => item === value));
            }

            return this.items.filter(({ value }) => this.selectModel === value);
        },
        listItemIconData() {
            return {
                iconName: 'check',
                iconSize: AllSizes.LARGE,
            };
        },
        togglerData() {
            return {
                ...this.themeData,
                bordered: this.bordered,
                borderTheme: this.borderTheme,
                colorTheme: this.colorTheme,
                disabled: this.disabled,
                hoverBorder: this.hoverBorder,
                static: true,
                size: AllSizes.MEDIUM,
                text: this.selectedItems?.[0]?.label || this.placeholder,
            };
        },
        togglerIconData() {
            return {
                class: {
                    'fa-rotate-180': this.isPopupVisible,
                },
                iconName: 'chevron-down',
                iconSize: AllSizes.LARGE,
            };
        },
        isPopupVisible: {
            get() {
                return this.opened;
            },
            set(value) {
                /**
                 * Эмит события `opened`, отвечает за открытие попапа
                 */
                this.$emit('update:opened', value);
            },
        },
        popupData() {
            const popupClass = 'sbs-select__popup';

            return {
                ...DEFAULT_POPUP_OPTIONS,
                ...this.popupProps,
                ...this.popupListeners,
                popupClass: this.popupProps.popupClass
                    ? `${popupClass} ${this.popupProps.popupClass}`
                    : popupClass,
            };
        },
    },
    methods: {
        isItemSelected(value) {
            let selected = false;

            if (Array.isArray(this.selectModel)) {
                selected = this.selectModel.includes(value);
            } else if (typeof this.selectModel === 'string') {
                selected = this.selectModel === value;
            }

            return selected;
        },
        scrollHandler(e) {
            /**
             * Эмит события `scroll`, срабатывает при скролле
             */
            this.$emit('scroll', e);
        },
        scrollReachEndHandler(e) {
            /**
             * Эмит события `scroll:reachEnd`, срабатывает, когда начинается скролл
             */
            this.$emit('scroll:reachEnd', e);
        },
        scrollReachStartHandler(e) {
            /**
             * Эмит события `scroll:reachEnd`, срабатывает, когда заканчивается скролл
             */
            this.$emit('scroll:reachStart', e);
        },
        keydownHandler(event) {
            const availableKeys = [Keys.ARROW_UP, Keys.ARROW_DOWN, Keys.HOME, Keys.END];

            if (!availableKeys.includes(event.key)) {
                return;
            }

            event.preventDefault();

            this.setFocusedIndex(event.key);

            const focusedItem = this.$refs.focusedItem?.[this.focusedIndex]?.$refs?.checkbox?.$refs?.input;

            if (focusedItem) {
                focusedItem.focus();
            }

            /**
             * Эмит события `focusedIndex:change`, срабатывает для выбора элемента из списка с помощью клавиатуры
             */
            this.$emit('focusedIndex:change', this.focusedIndex);
        },
        setFocusedIndex(key) {
            if (key === Keys.ARROW_UP) {
                if (this.focusedIndex > 0) {
                    this.focusedIndex -= 1;
                } else {
                    this.focusedIndex = this.items.length - 1;
                }
            }

            if (key === Keys.ARROW_DOWN) {
                if (this.focusedIndex < this.items.length - 1) {
                    this.focusedIndex += 1;
                } else {
                    this.focusedIndex = 0;
                }
            }

            if (key === Keys.HOME) {
                this.focusedIndex = 0;
            }

            if (key === Keys.END) {
                this.focusedIndex = this.items.length - 1;
            }
        },
        /** @public */
        updateFocusedIndex() {
            this.focusedIndex = -1;
        },
    },
    props: {
        /**
         * Определяет наличие бордера у кнопки
         */
        bordered: {
            type: Boolean,
            default: true,
        },
        /**
         * Тема (цветовые решения) для цвета бордера
         */
        borderTheme: {
            type: String,
            default: AllColors.GRAY_LIGHT,
        },
        /**
         * Тема (цветовые решения) для цвета текста
         */
        colorTheme: {
            type: String,
            default: AllColors.BLACK,
        },
        /**
         * Отключает взаимодествие с селектом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для ховера бордера
         */
        hoverBorder: {
            type: String,
            default: AllColors.SUCCESS,
        },
        /**
         * Значения пунктов списка
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * Включает принудительный выбор элемента
         */
        mandatory: {
            type: Boolean,
            default: false,
        },
        /**
         * Раскрывает попап со списком
         */
        opened: {
            type: Boolean,
            default: false,
        },
        /**
         * Текст внутри кнопки-активатора, если значение не выбрано
         */
        placeholder: {
            type: String,
            default: '',
        },
        /**
         * Попап принимает несколько событий
         */
        popupListeners: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Получает все пропсы попапа
         */
        popupProps: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Класс для списка элементов
         */
        selectListGroupClass: {
            type: String,
            default: '',
        },
        /**
         * Значение элемента в списке
         * @model
         */
        modelValue: {
            type: [String, Array],
            default: '',
        },
    },
    watch: {
        isPopupVisible(value) {
            if (value) {
                document.addEventListener('keydown', this.keydownHandler);
            } else {
                document.removeEventListener('keydown', this.keydownHandler);
            }
        },
    },
};
</script>

<style lang="scss">
.sbs-select {
    position: relative;
    z-index: 100;
    display: inline-flex;
    flex-direction: column;
    max-width: 100%;

    &__popup {
        padding: 15px 0;
    }

    &__toggler-button {
        border-radius: $rounded-10;
    }

    &__toggler-icon {
        margin-left: 10px;
        border-radius: $rounded-circle;
    }

    &__list-item {
        &.selected {
            background-color: $additional;
        }
    }

    &__list-item-label {
        padding-right: 20px;
        padding-left: 20px;
        border-radius: $rounded-0;
    }

    &__list-item-inner {
        display: inline-flex;
    }

    &__list-item-text {
        margin-right: auto;
    }

    &__list-item-icon {
        margin-left: 20px;
        opacity: 0;

        .sbs-select__list-item.selected & {
            opacity: 1;
        }
    }

    &.full-width {
        .sbs-select__toggler {
            width: 100%;
        }
    }
}
</style>
